import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  LogOut, Building2, Home, Loader2, RefreshCw, Clock,
  CheckCircle, XCircle, User, Globe2, FileText, MessageSquare
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusColors = { pending: "bg-yellow-100 text-yellow-700", accepted: "bg-blue-100 text-blue-700", "in-treatment": "bg-purple-100 text-purple-700", completed: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700" };
const statusOptions = ["pending", "accepted", "in-treatment", "completed", "rejected"];

export default function PartnerDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(null);
  const [noteText, setNoteText] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/dashboard`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/partner/login");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "partner") { navigate("/partner/login"); return; }
    fetchData();
  }, [user, authLoading, navigate, fetchData]);

  const updateReferral = async (id, updates) => {
    try {
      await axios.patch(`${API}/partner/referrals/${id}`, updates, { withCredentials: true });
      toast.success("Updated"); fetchData(); setEditingNotes(null);
    } catch { toast.error("Update failed"); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  if (authLoading || loading) return <div className="min-h-[80vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00A4A9]" /></div>;

  return (
    <div data-testid="partner-dashboard" className="min-h-screen bg-[#F9F9F8]">
      <div className="bg-[#03045e] text-white px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="w-8 h-8 object-contain" />
            <div>
              <h1 className="font-outfit text-lg font-medium" data-testid="partner-title">Partner Portal</h1>
              <p className="text-sm text-white/50">{data?.profile?.hospital_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-white hover:bg-white/10"><RefreshCw className="w-4 h-4" /></Button>
            <Link to="/"><Button variant="ghost" size="sm" className="text-white hover:bg-white/10"><Home className="w-4 h-4 mr-1.5" />Home</Button></Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="partner-logout"><LogOut className="w-4 h-4 mr-1.5" />Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {/* Stats */}
        {data?.stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="partner-stats">
            {[
              { l: "Total Referrals", v: data.stats.total, icon: FileText, c: "text-[#00A4A9]" },
              { l: "Pending", v: data.stats.pending, icon: Clock, c: "text-yellow-600" },
              { l: "Accepted", v: data.stats.accepted, icon: CheckCircle, c: "text-blue-600" },
              { l: "Completed", v: data.stats.completed, icon: CheckCircle, c: "text-green-600" },
            ].map((s) => (
              <div key={s.l} className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="flex items-center justify-between mb-2"><span className="text-xs text-slate-500">{s.l}</span><s.icon className={`w-4 h-4 ${s.c}`} /></div>
                <div className="font-outfit text-2xl font-semibold text-[#03045e]">{s.v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Referrals */}
        <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-4">Patient Referrals</h3>
        {(!data?.referrals || data.referrals.length === 0) ? (
          <div className="bg-white rounded-xl border p-12 text-center" data-testid="no-referrals">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No referrals yet.</p>
            <p className="text-xs text-slate-400 mt-1">Referrals from Flarial MediTours will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="referrals-list">
            {data.referrals.map((ref) => (
              <div key={ref.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`referral-${ref.id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-outfit font-medium text-[#03045e]">{ref.patient_name}</h4>
                      <Badge className={statusColors[ref.status] || "bg-slate-100"}>{ref.status}</Badge>
                      <Badge variant="outline" className="text-xs">{ref.category}</Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-slate-600 mb-2">
                      <span className="flex items-center gap-1"><Globe2 className="w-3.5 h-3.5 text-slate-400" /> {ref.patient_country}</span>
                      <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-slate-400" /> {ref.treatment}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" /> {new Date(ref.created_at).toLocaleDateString()}</span>
                    </div>
                    {ref.details && <p className="text-sm text-slate-500 bg-slate-50 rounded-lg p-3 mt-2">{ref.details}</p>}
                    {ref.partner_notes && (
                      <div className="mt-2 bg-blue-50 rounded-lg p-3">
                        <p className="text-xs text-blue-600 font-medium mb-1">Your Notes:</p>
                        <p className="text-sm text-blue-800">{ref.partner_notes}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 min-w-[160px]">
                    <select value={ref.status} onChange={(e) => updateReferral(ref.id, { status: e.target.value })}
                      className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-[#00A4A9]"
                      data-testid={`status-${ref.id}`}>
                      {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {editingNotes === ref.id ? (
                      <div className="space-y-2">
                        <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add notes..." className="text-xs min-h-[60px]" data-testid={`notes-input-${ref.id}`} />
                        <div className="flex gap-1">
                          <Button size="sm" className="flex-1 bg-[#00A4A9] text-white text-xs rounded-lg" onClick={() => updateReferral(ref.id, { partner_notes: noteText })} data-testid={`save-notes-${ref.id}`}>Save</Button>
                          <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setEditingNotes(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => { setEditingNotes(ref.id); setNoteText(ref.partner_notes || ""); }} data-testid={`add-notes-${ref.id}`}>
                        <MessageSquare className="w-3 h-3 mr-1" /> {ref.partner_notes ? "Edit Notes" : "Add Notes"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
